import AdaptivePersonaControl from '../controls/AdaptivePersonaControl';
import CustomChoiceSetInput from '../controls/CustomChoiceSetInput';
import CustomDateInput from '../controls/CustomDateInput';
import CustomNumberInput from '../controls/CustomNumberInput';
import CustomTextInput from '../controls/CustomTextInput';
import CustomToggleInput from '../controls/CustomToggleInput';
import CustomImage from '../controls/CustomImage';
import CustomTextBlock from '../controls/CustomTextBlock';
import ActionProvider from '../store/schema/ActionProvider';
import { initialize as initializeCustomImageSet } from '../controls/CustomImageSet';
import {
    AdaptiveCard,
    Spacing,
    ActionSet,
    ColumnSet,
    Image,
    IValidationError,
    IMarkdownProcessingResult,
    Container,
    PaddingDefinition,
    getEnumValue,
} from 'adaptivecards';
import * as MarkdownIt from 'markdown-it';
import { trace } from 'owa-trace';
import ActionableMessageCardStrings from '../store/schema/ActionableMessageCardStrings';

let initialized: boolean = false;

const registerTypes = (cardStrings: ActionableMessageCardStrings) => {
    CustomDateInput.strings = cardStrings;
    AdaptiveCard.elementTypeRegistry.registerType('Input.Date', () => {
        return new CustomDateInput();
    });

    CustomTextInput.strings = cardStrings;
    AdaptiveCard.elementTypeRegistry.registerType('Input.Text', () => {
        return new CustomTextInput();
    });

    CustomNumberInput.strings = cardStrings;
    AdaptiveCard.elementTypeRegistry.registerType('Input.Number', () => {
        return new CustomNumberInput();
    });

    CustomToggleInput.strings = cardStrings;
    AdaptiveCard.elementTypeRegistry.registerType('Input.Toggle', () => {
        return new CustomToggleInput();
    });

    CustomChoiceSetInput.strings = cardStrings;
    AdaptiveCard.elementTypeRegistry.registerType('Input.ChoiceSet', () => {
        return new CustomChoiceSetInput();
    });

    AdaptiveCard.elementTypeRegistry.registerType('TextBlock', () => {
        return new CustomTextBlock();
    });

    AdaptiveCard.elementTypeRegistry.registerType('Image', () => {
        return new CustomImage();
    });

    AdaptiveCard.elementTypeRegistry.registerType('Persona', () => {
        return new AdaptivePersonaControl();
    });

    AdaptiveCard.elementTypeRegistry.registerType('ActionSet', () => {
        return new ActionSet();
    });
};

const processMarkdown = (text: string, result: IMarkdownProcessingResult) => {
    const markdownRenderOutput = MarkdownIt().render(text);
    result.didProcess = true;
    result.outputHtml = markdownRenderOutput;
};

const parseError = (error: IValidationError) => {
    trace.warn(
        '[ActionableMessageDebug] parseError: Error parsing adaptive card. Error: ' + error.message
    );
};

const parseElement = (element: any, json: any) => {
    if (typeof json.isVisible === 'boolean') {
        element.isVisible = json.isVisible;
    }

    const elementType = element.getJsonTypeName();
    if (elementType === 'Column' && typeof json.pixelWidth !== 'undefined') {
        element.pixelWidth = json.pixelWidth;
    }

    if (element instanceof Image && typeof json.backgroundColor === 'string') {
        element.backgroundColor = json.backgroundColor;
    }

    if (element instanceof Container) {
        const padding = parsePadding(json.padding);

        if (padding) {
            (element as Container).padding = padding;
        }
    }

    if (element instanceof ColumnSet) {
        const padding = parsePadding(json.padding);

        if (padding) {
            (element as ColumnSet).padding = padding;
        }
    }
};

const registerCustomActions = (actionProviders: ActionProvider[]) => {
    actionProviders.forEach(actionProvider => {
        if (actionProvider) {
            actionProvider.registerActions();
        }
    });
};

const parsePadding = (jsonPadding: any): PaddingDefinition => {
    if (typeof jsonPadding === 'string') {
        const uniformPadding = getEnumValue(Spacing, jsonPadding, Spacing.None);

        return new PaddingDefinition(
            uniformPadding,
            uniformPadding,
            uniformPadding,
            uniformPadding
        );
    } else if (typeof jsonPadding === 'object') {
        return new PaddingDefinition(
            getEnumValue(Spacing, jsonPadding.top, Spacing.None),
            getEnumValue(Spacing, jsonPadding.right, Spacing.None),
            getEnumValue(Spacing, jsonPadding.bottom, Spacing.None),
            getEnumValue(Spacing, jsonPadding.left, Spacing.None)
        );
    }

    return null;
};

export default async function initializeActionableMessageLibrary(
    actionProviders: ActionProvider[],
    cardStrings: ActionableMessageCardStrings
) {
    if (!initialized) {
        initialized = true;
        AdaptiveCard.allowMarkForTextHighlighting = true;
        AdaptiveCard.onParseError = parseError;
        AdaptiveCard.onProcessMarkdown = processMarkdown;
        AdaptiveCard.onParseElement = parseElement;
        registerTypes(cardStrings);
        initializeCustomImageSet();
    }

    registerCustomActions(actionProviders);
}
